import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { IoClose } from 'react-icons/io5';
import Switch from './atoms/Switch';
import Input from './atoms/Input';
import Button from './atoms/Button';
import {
  useCreateEventMutation,
  useCreateEventSubscriptionMutation,
  useDeleteEventBySuscribedIdMutation,
  useGetCategoriesByEventIdQuery,
  useGetCategoriesQuery,
  useGetEventSubscriptionQuery,
  useUpdateEventMutation,
  useCreateEventCategoryMutation,
  useDeleteEventCategoryMutation,
} from '../store';
import FormControlTextArea from './atoms/FormControlTextArea';
import { setSelectedEvent } from '../store/slices/events';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import useGetUserRole from '../hooks/useGetUserRole';
import LoadingLottie from './atoms/LoadingLottie';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import PhotoUpload from './PhotoUpload';
import ErrorMessage from './atoms/ErrorMessage';
import { formatDateForInput } from '../utils/dates';
import CustomModal from './atoms/CustomModal';
import EventDTO, { CategoryDTO } from '../models/event';
import Accordion from './atoms/Accordion';
import Select from './atoms/Select/Select';
import { toastError, toastSuccess } from '../utils/toasts';
import HorizontalTabs from './atoms/HorizontalTabs';

interface Props {
  create: boolean;
}

interface FormData {
  name: string;
  description: string;
  date: string;
  ends_at: string;
  place: string;
  terms: string | undefined;
  phone: string;
  min_age: number;
  address: string;
  require_id_tickets: boolean;
  is_public: boolean;
  active: boolean;
  banner_image: string;
  seller_sales_enabled: boolean;
  seller_can_download_tickets: boolean;
  has_order_referrals: 'enabled' | 'disabled' | 'enabled_unaccepted';
  business_name: string;
  // ROOT only fields
  meta_pixel_id?: string | null;
  maps_link?: string | undefined;
  allow_mercadopago_payments?: boolean;
  allow_payway_payments?: boolean;
  allow_uala_payments?: boolean;
  allow_nave_payments?: boolean;
  allow_zilo_payments?: boolean;
  allow_manual_payments?: boolean;
  allow_in_person_sales?: boolean;
  online_service_charge?: number;
  manual_service_charge?: number;
  online_comission_rate?: number;
  receives_wire_transfers?: boolean;
  has_polls?: boolean;
  has_numerated_seats?: boolean;
  tickets_includes_service_charge?: boolean;
  has_seller_qr?: boolean;
  has_scanner_auto_accept?: boolean;
  has_order_qrs?: boolean;
  is_important?: boolean;
  is_approved?: boolean;
  has_audience_discounts?: boolean;
  has_open_price?: boolean;
  has_simplified_selling?: boolean;
  has_showtimes?: boolean;
  has_subscription?: boolean;
  notify_purchases?: boolean;
  superfast_checkout?: boolean;
  show_sellers_ranking?: boolean;
  has_lists?: boolean;
  producer_absorbs_service_charge?: boolean;
  absorbed_service_charge_rate?: number;
  order_phone_number?: boolean;
  coproducer_reports?: boolean;
  coproducer_balance?: boolean;
}

const EventEditor: React.FC<Props> = ({ create }) => {
  const navigate = useNavigateWithQuery();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRole = useGetUserRole();
  const { event_id: eventId, business_id: businessId } = useParams<{
    event_id: string;
    business_id: string;
  }>();
  const { business, businessLoading } = useGetSelectedBusiness();
  const { event, eventLoading } = useGetSelectedEvent({ skip: !eventId });
  const { data: subscribedData, refetch: refetchSubscribedData } = useGetEventSubscriptionQuery({ businessId: businessId || '', eventId: eventId || '' }, { skip: !business?.has_subscription || create });
  const { data: categories } = useGetCategoriesQuery();
  const { data: eventCategories } = useGetCategoriesByEventIdQuery({ eventId: eventId || '' }, { skip: true }); // skip retrivieng categories if its not needed (editor doesn't need it)
  const [createEventCategory] = useCreateEventCategoryMutation();
  const [deleteEventCategory] = useDeleteEventCategoryMutation();
  const isOnBoarding = location.pathname.includes('onboarding');
  const [isOnBoardingModalOpen, setIsOnBoardingModalOpen] = useState(false);
  const [createdEvent, setCreatedEvent] = useState<EventDTO>();
  const [subscribedEvents, setSubscribedEvents] = useState<string[]>([]);
  const [createEvent, resCreateEvent] = useCreateEventMutation();
  const [updateEvent, resUpdateEvent] = useUpdateEventMutation();
  const [createEventSubscription] = useCreateEventSubscriptionMutation();
  const [deleteEventSubscription] = useDeleteEventBySuscribedIdMutation();
  const [category, setCategory] = useState<CategoryDTO[]>([]);
  const [tab, setTab] = useState<'general' | 'payments'>('general');

  const getValidationSchema = () => {
    const baseSchema: { [K in keyof FormData]?: yup.AnySchema } = {
      name: yup.string().required('Debes ingresar un nombre'),
      description: yup.string().required('Debes ingresar una descripción'),
      date: yup.string().required('Debes ingresar una fecha'),
      ends_at: yup.string()
        .required('Debes ingresar una fecha de fin')
        .test(
          'is-after-start',
          'La fecha de fin debe ser posterior a la fecha de inicio',
          (value, testContext) => {
            const start = testContext.parent.date;
            if (!start || !value) {
              return true;
            }
            return new Date(value) > new Date(start);
          },
        ),
      place: yup.string().required('Debes ingresar un lugar'),
      require_id_tickets: yup.boolean().required(),
      is_public: yup.boolean().required(),
      active: yup.boolean().required(),
      terms: yup.string().optional(),
      phone: yup.string().required('Debes ingresar un teléfono'),
      min_age: yup
        .number()
        .required('Debes ingresar una edad mínima')
        .typeError('Debe ser un número'),
      address: yup.string().required('Debes ingresar una dirección'),
      banner_image: yup
        .string()
        .url('La imagen no tiene una URL válida')
        .required('Debes subir una imagen'),
      seller_sales_enabled: yup.boolean().required(),
      seller_can_download_tickets: yup.boolean().required(),
      has_order_referrals: yup.string().required(),
      has_scanner_auto_accept: yup.boolean().required(),
      has_order_qrs: yup.boolean().required(),
      business_name: yup.string().required('Debes ingresar un nombre de productora'),
    };

    if (userRole === 'ROOT') {
      return yup.object().shape({
        ...baseSchema,
        meta_pixel_id: yup.string().nullable().optional(),
        maps_link: yup.string().url('Debe ser una URL válida').optional(),
        allow_mercadopago_payments: yup.boolean().required(),
        allow_payway_payments: yup.boolean().required(),
        allow_uala_payments: yup.boolean().required(),
        allow_nave_payments: yup.boolean().required(),
        allow_zilo_payments: yup.boolean().required(),
        allow_manual_payments: yup.boolean().required(),
        allow_in_person_sales: yup.boolean().required(),
        online_service_charge: yup
          .number()
          .required('Debes ingresar un cargo por servicio')
          .min(0, 'El cargo por servicio debe ser al menos 0')
          .max(1, 'El cargo por servicio no puede ser mayor que 1')
          .typeError('Debe ser un número'),
        manual_service_charge: yup
          .number()
          .required('Debes ingresar un cargo por servicio')
          .min(0, 'El cargo por servicio debe ser al menos 0')
          .max(1, 'El cargo por servicio no puede ser mayor que 1')
          .typeError('Debe ser un número'),
        online_comission_rate: yup
          .number()
          .required('Debes ingresar un cargo por servicio')
          .min(0, 'El cargo por servicio debe ser al menos 0')
          .max(1, 'El cargo por servicio no puede ser mayor que 1')
          .typeError('Debe ser un número'),
        receives_wire_transfers: yup.boolean().required(),
        has_polls: yup.boolean().required(),
        has_numerated_seats: yup.boolean().required(),
        tickets_includes_service_charge: yup.boolean().required(),
        producer_absorbs_service_charge: yup.boolean().required(),
        absorbed_service_charge_rate: yup
          .number()
          .nullable()
          .when('producer_absorbs_service_charge', {
            is: true,
            then: (schema) => schema
              .required('Debes ingresar un cargo absorbido')
              .min(0, 'El cargo absorbido debe ser al menos 0')
              .max(1, 'El cargo absorbido no puede ser mayor que 1')
              .typeError('Debe ser un número'),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
        has_seller_qr: yup.boolean().required(),
        is_important: yup.boolean().required(),
        is_approved: yup.boolean().required(),
        has_audience_discounts: yup.boolean().required(),
        has_open_price: yup.boolean().required(),
        has_simplified_selling: yup.boolean().required(),
        has_showtimes: yup.boolean().required(),
        has_subscription: yup.boolean().required(),
        notify_purchases: yup.boolean().required(),
        superfast_checkout: yup.boolean().required(),
        show_sellers_ranking: yup.boolean().required(),
        order_phone_number: yup.string().required('Debes ingresar un número de teléfono'),
        has_lists: yup.boolean().required(),
        coproducer_reports: yup.boolean().required(),
        coproducer_balance: yup.boolean().required(),
      });
    }

    if (userRole === 'ADMIN') {
      return yup.object().shape({
        ...baseSchema,
        coproducer_reports: yup.boolean().required(),
        coproducer_balance: yup.boolean().required(),
      });
    }

    return yup.object().shape(baseSchema);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      name: '',
      description: '',
      date: '',
      ends_at: '',
      place: '',
      terms: '',
      phone: business?.phone_number || '',
      min_age: 0,
      address: '',
      require_id_tickets: false,
      is_public: false,
      active: true,
      banner_image: 'https://storage.googleapis.com/avantti-images/eventimg_59d38c70-fcb5-4ae1-9c57-4f8733345d78_avantti-default-eventpng',
      seller_sales_enabled: false,
      seller_can_download_tickets: false,
      has_order_referrals: 'disabled',
      has_scanner_auto_accept: false,
      has_order_qrs: false,
      business_name: business?.name || '',
      ...(userRole === 'ADMIN' && {
        coproducer_reports: false,
        coproducer_balance: false,
      }),
      ...(userRole === 'ROOT' && {
        meta_pixel_id: null,
        maps_link: '',
        allow_mercadopago_payments: true,
        allow_payway_payments: true,
        allow_uala_payments: false,
        allow_nave_payments: false,
        allow_zilo_payments: false,
        allow_manual_payments: false,
        allow_in_person_sales: false,
        online_service_charge: business?.online_service_charge ?? 0.15,
        manual_service_charge: business?.manual_service_charge ?? 0.05,
        online_comission_rate: business?.online_comission_rate ?? 0.05,
        receives_wire_transfers: false,
        has_polls: false,
        has_numerated_seats: false,
        tickets_includes_service_charge: false,
        producer_absorbs_service_charge: false,
        absorbed_service_charge_rate: 0,
        has_seller_qr: false,
        is_important: false,
        is_approved: business?.is_reliable ?? false,
        has_audience_discounts: false,
        has_open_price: false,
        has_simplified_selling: false,
        has_showtimes: false,
        has_subscription: false,
        notify_purchases: false,
        superfast_checkout: false,
        show_sellers_ranking: false,
        order_phone_number: false,
        has_lists: false,
        coproducer_reports: false,
        coproducer_balance: false,
      }),
    },
    resolver: yupResolver(getValidationSchema() as yup.ObjectSchema<FormData>),
    mode: 'onChange',
  });

  const ticketsIncludesServiceCharge = watch('tickets_includes_service_charge');
  const producerAbsorbsServiceCharge = watch('producer_absorbs_service_charge');
  const onlineServiceCharge = watch('online_service_charge');

  useEffect(() => {
    if (business?.has_subscription && !create) {
      refetchSubscribedData();
    }
  }, [business?.has_subscription, create, refetchSubscribedData]);

  useEffect(() => {
    if (!ticketsIncludesServiceCharge) {
      setValue('producer_absorbs_service_charge', false);
      setValue('absorbed_service_charge_rate', 0);
    }
  }, [ticketsIncludesServiceCharge, setValue]);

  useEffect(() => {
    if (ticketsIncludesServiceCharge && producerAbsorbsServiceCharge && onlineServiceCharge !== undefined) {
      setValue('absorbed_service_charge_rate', onlineServiceCharge, { shouldValidate: true, shouldDirty: true });
    }
  }, [ticketsIncludesServiceCharge, producerAbsorbsServiceCharge, onlineServiceCharge, setValue]);

  useEffect(() => {
    if (subscribedData) {
      setSubscribedEvents(subscribedData.map((s) => s.subscribed_event_id));
    }
  }, [subscribedData]);

  useEffect(() => {
    if (eventCategories) {
      setCategory(eventCategories.data?.map((c) => c.category) || []);
    }
  }, [eventCategories]);

  useEffect(() => {
    if (event) {
      reset({
        name: event.name || '',
        description: event.description || '',
        date: event.date ? formatDateForInput(event.date) ?? '' : formatDateForInput(new Date().toISOString()) ?? '',
        ends_at: event.ends_at
          ? formatDateForInput(event.ends_at) ?? ''
          : formatDateForInput(new Date().toISOString()) ?? '',
        place: event.place || '',
        require_id_tickets: event.require_id_tickets || false,
        terms: event.terms || '',
        phone: event.phone || business?.phone_number || '',
        min_age: event.min_age || 0,
        address: event.address || '',
        is_public: event.is_public || false,
        active: event.active,
        banner_image: event.banner_image || 'https://storage.googleapis.com/avantti-images/eventimg_59d38c70-fcb5-4ae1-9c57-4f8733345d78_avantti-default-eventpng',
        seller_sales_enabled: event.seller_sales_enabled ?? false,
        seller_can_download_tickets: event.seller_can_download_tickets ?? false,
        has_order_referrals: event.has_order_referrals ?? 'disabled',
        has_scanner_auto_accept: event.has_scanner_auto_accept ?? false,
        has_order_qrs: event.has_order_qrs ?? false,
        business_name: event.business_name || business?.name || '',
        ...(userRole === 'ADMIN' && {
          coproducer_reports: event.coproducer_reports ?? false,
          coproducer_balance: event.coproducer_balance ?? false,
        }),
        ...(userRole === 'ROOT' && {
          meta_pixel_id: event.meta_pixel_id ?? null,
          maps_link: event.maps_link,
          allow_mercadopago_payments: event.allow_mercadopago_payments ?? false,
          allow_payway_payments: event.allow_payway_payments ?? false,
          allow_uala_payments: event.allow_uala_payments ?? false,
          allow_nave_payments: event.allow_nave_payments ?? false,
          allow_zilo_payments: event.allow_zilo_payments ?? false,
          allow_manual_payments: event.allow_manual_payments,
          allow_in_person_sales: event.allow_in_person_sales,
          online_service_charge: event.online_service_charge ?? business?.online_service_charge,
          manual_service_charge: event.manual_service_charge ?? business?.manual_service_charge,
          online_comission_rate: event.online_comission_rate ?? business?.online_comission_rate,
          receives_wire_transfers: event.receives_wire_transfers,
          has_polls: event.has_polls,
          has_numerated_seats: event.has_numerated_seats ?? false,
          tickets_includes_service_charge: event.tickets_includes_service_charge ?? false,
          producer_absorbs_service_charge: Boolean(event.absorbed_service_charge_rate && event.absorbed_service_charge_rate > 0),
          absorbed_service_charge_rate: event.absorbed_service_charge_rate ?? 0,
          has_seller_qr: event.has_seller_qr ?? false,
          is_important: event.is_important ?? false,
          is_approved: event.is_approved ?? false,
          has_audience_discounts: event.has_audience_discounts ?? false,
          has_open_price: event.has_open_price ?? false,
          has_simplified_selling: event.has_simplified_selling ?? false,
          has_showtimes: event.has_showtimes ?? false,
          has_subscription: event.has_subscription ?? false,
          notify_purchases: event.notify_purchases ?? false,
          superfast_checkout: event.superfast_checkout ?? false,
          show_sellers_ranking: event.show_sellers_ranking ?? false,
          order_phone_number: event.order_phone_number ?? false,
          has_lists: event.has_lists ?? false,
          coproducer_reports: event.coproducer_reports ?? false,
          coproducer_balance: event.coproducer_balance ?? false,
        }),
      });
    }
  }, [event, reset, userRole, business?.phone_number, business?.name, eventCategories, eventLoading, business?.is_reliable, business?.online_service_charge, business?.manual_service_charge, business?.online_comission_rate]);

  useEffect(() => {
    if (resUpdateEvent.isSuccess || resCreateEvent.isSuccess) {
      toastSuccess(`Evento ${create ? 'creado' : 'actualizado'} con éxito`);
      if (resUpdateEvent.data?.data) {
        dispatch(setSelectedEvent(resUpdateEvent.data.data));
      }
      if (!isOnBoarding) {
        navigate('..');
      } else {
        setIsOnBoardingModalOpen(true);
      }
    }
  }, [resCreateEvent.isSuccess, resUpdateEvent.isSuccess, create, isOnBoarding]);

  useEffect(() => {
    if (business?.phone_number) {
      setValue('phone', business.phone_number);
    }
    if (business?.name && !event?.business_name) {
      setValue('business_name', business.name);
    }
  }, [business, setValue, event?.business_name]);

  const formDataToDto = (data: FormData) => {
    const baseEventData = {
      name: data.name,
      description: data.description,
      date: new Date(data.date).toISOString(),
      ends_at: new Date(data.ends_at).toISOString(),
      place: data.place,
      terms: data.terms,
      phone: data.phone,
      min_age: data.min_age,
      address: data.address,
      require_id_tickets: data.require_id_tickets,
      banner_image: data.banner_image,
      is_public: data.is_public,
      active: data.active,
      seller_sales_enabled: data.seller_sales_enabled,
      seller_can_download_tickets: data.seller_can_download_tickets,
      has_order_referrals: data.has_order_referrals ?? 'disabled',
      has_scanner_auto_accept: data.has_scanner_auto_accept,
      has_order_qrs: data.has_order_qrs,
      business_name: data.business_name,
      ...(userRole === 'ADMIN' && {
        coproducer_reports: data.coproducer_reports,
        coproducer_balance: data.coproducer_balance,
      }),
    };
    if (userRole !== 'ROOT') {
      return baseEventData;
    }
    return {
      ...baseEventData,
      meta_pixel_id: data.meta_pixel_id || null,
      maps_link: data.maps_link,
      allow_mercadopago_payments: data.allow_mercadopago_payments,
      allow_payway_payments: data.allow_payway_payments,
      allow_uala_payments: data.allow_uala_payments,
      allow_nave_payments: data.allow_nave_payments,
      allow_zilo_payments: data.allow_zilo_payments,
      allow_manual_payments: data.allow_manual_payments,
      allow_in_person_sales: data.allow_in_person_sales,
      online_service_charge: data.online_service_charge,
      manual_service_charge: data.manual_service_charge,
      online_comission_rate: data.online_comission_rate,
      receives_wire_transfers: data.receives_wire_transfers,
      has_polls: data.has_polls,
      has_numerated_seats: data.has_numerated_seats,
      tickets_includes_service_charge: data.tickets_includes_service_charge,
      has_seller_qr: data.has_seller_qr,
      is_important: data.is_important,
      is_approved: data.is_approved,
      has_audience_discounts: data.has_audience_discounts,
      has_open_price: data.has_open_price,
      has_simplified_selling: data.has_simplified_selling,
      has_showtimes: data.has_showtimes,
      has_subscription: data.has_subscription,
      notify_purchases: data.notify_purchases,
      superfast_checkout: data.superfast_checkout,
      show_sellers_ranking: data.show_sellers_ranking,
      has_lists: data.has_lists,
      absorbed_service_charge_rate: data.tickets_includes_service_charge ? data.online_service_charge : data.absorbed_service_charge_rate ?? 0,
      order_phone_number: data.order_phone_number,
      coproducer_reports: data.coproducer_reports,
      coproducer_balance: data.coproducer_balance,
    };
  };

  const onSubmit = async (data: FormData) => {
    if (!businessId) {
      throw new Error('business not found');
    }
    try {
      const eventData = formDataToDto(data);

      if (event) {
        await updateEvent({
          event: { ...eventData, id: eventId ?? '' },
          businessId: businessId || '',
        });
        if (data.has_subscription) {
          const eventsToSubscribe = subscribedEvents.filter((se) => se !== subscribedData?.find((s) => s.subscribed_event_id === se)?.subscribed_event_id);
          const eventsToUnsubscribe = subscribedData?.filter((s) => !subscribedEvents.includes(s.subscribed_event_id)).map((s) => s.subscribed_event_id) || [];
          Promise.all(eventsToSubscribe.map((subscribedEvent) => createEventSubscription({
            businessId: businessId || '',
            eventId: eventId || '',
            subscriptionData: { subscribed_event_id: subscribedEvent },
          })));
          Promise.all(eventsToUnsubscribe?.map((unsubscribedEvent) => deleteEventSubscription({
            businessId: businessId || '',
            eventId: eventId || '',
            suscribedId: unsubscribedEvent,
          })));
        }
        // Sync categories
        const currentCategoryIds = eventCategories?.data?.map((ec) => ec.category.id) || [];
        const selectedCategoryIds = category.map((c) => c.id);

        // Find categories to add (in selected but not in current)
        const categoriesToAdd = selectedCategoryIds.filter((id) => !currentCategoryIds.includes(id));

        // Find event categories to remove (in current but not in selected)
        const eventCategoriesToRemove = eventCategories?.data?.filter((ec) => !selectedCategoryIds.includes(ec.category.id)) || [];

        // Create new category associations
        if (categoriesToAdd.length > 0) {
          await Promise.all(categoriesToAdd.map((categoryId) => createEventCategory({
            eventId: eventId || '',
            categoryId,
          })));
        }

        // Remove category associations using eventCategory.id
        if (eventCategoriesToRemove.length > 0) {
          await Promise.all(eventCategoriesToRemove.map((eventCategory) => deleteEventCategory({
            eventId: eventId || '',
            eventCategoryId: eventCategory.id, // Using eventCategory.id (the relationship ID)
          })));
        }
      } else {
        const res = await createEvent({
          event: eventData,
          businessId: businessId || '',
        });
        if ('data' in res && res.data.data) {
          setCreatedEvent(res.data.data);

          // Create category associations for new event
          if (category.length > 0) {
            await Promise.all(category.map((c) => createEventCategory({
              eventId: res.data.data?.id || '',
              categoryId: c.id,
            })));
          }
        }
      }
    } catch (e) {
      console.error(e);
      toastError('Ocurrió un error');
    }
  };

  if (eventLoading || businessLoading) {
    return <LoadingLottie />;
  }

  const options = business?.events?.filter((e) => {
    if (e.id === eventId) {
      return false;
    }

    const isNewlySelected = subscribedEvents.includes(e.id);
    if (isNewlySelected) {
      return false;
    }

    return true;
  }).map((e) => ({
    label: e.name,
    value: e.id,
  })) || [];

  return (
    <div className={`${isOnBoarding ? '' : 'px-3 my-5'}`}>
      {!isOnBoarding && <div className="text-3xl font-bold text-text-primary">
        {create ? 'Crear nuevo evento' : 'Editar evento'}
      </div>}
      {!isOnBoarding && <div className="my-2 text-xl font-light text-text-primary">
        Productora: {business?.name}
      </div>}
      <form onSubmit={handleSubmit(onSubmit)} className="w-full h-full">
        {userRole === 'ROOT' && <div className="glass-surface px-3 pb-2 pt-1 mb-4">
          <HorizontalTabs tabs={[{ label: 'Datos generales', onClick: () => setTab('general'), selected: tab === 'general' }, { label: 'Pagos', onClick: () => setTab('payments'), selected: tab === 'payments' }]} />
        </div>}
        {
          tab === 'general' && (
            <>
              <div className="glass-surface">
                <div className="flex flex-col gap-3">
                  <div className="text-xl font-medium">Estado</div>
                  <div>
                    Los eventos activos están publicados y disponibles para la compra de
                    entradas
                  </div>
                  <Switch
                    label="Activo"
                    labelClassName="font-medium"
                    checked={watch('active')}
                    onChange={(e) => setValue('active', e.target.checked)}
                  />
                </div>
                <div className="flex flex-col gap-2 mt-4">
                  <div className="  text-xl font-medium">Visibilidad</div>
                  <div>
                    Los eventos públicos se muestran a todos los usuarios que navegan en
                    el sitio de Avantti
                  </div>
                  <Switch
                    label="Público"
                    labelClassName="font-medium"
                    checked={watch('is_public')}
                    onChange={(e) => setValue('is_public', e.target.checked)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-3 my-4 glass-surface relative z-10">
                <div className="font-medium text-xl">
                  Datos del evento
                </div>
                <Input
                  {...register('name')}
                  id="name"
                  label="Nombre del evento *"
                  labelAbove
                  placeholder=""
                  additionalClasses="bg-surface-base mb-4"
                  errMsg={errors.name?.message}
                />
                <FormControlTextArea
                  {...register('description')}
                  id="description"
                  label="Descripción *"
                  labelAbove
                  placeholder='Coloca una breve descripción sobre tu evento para que tus asistentes sepan de que se trata. Máximo 1500 caracteres.'
                  rows={5}
                  errMsg={errors.description?.message}
                />
                <FormControlTextArea
                  {...register('terms')}
                  id="terms"
                  label="Más información"
                  labelAbove
                  placeholder='En este apartado podés informar un poco más sobre horarios, line up, restricciones y demás. Máximo 1500 caracteres.'
                  rows={5}
                  errMsg={errors.terms?.message}
                />
                <div className="flex flex-col md:flex-row w-full gap-4">
                  <Input
                    {...register('date')}
                    id="date"
                    type="datetime-local"
                    label="Fecha y hora de inicio *"
                    labelAbove
                    placeholder=""
                    additionalClasses="bg-surface-base"
                    errMsg={errors.date?.message}
                  />
                  <Input
                    {...register('ends_at')}
                    id="ends_at"
                    type="datetime-local"
                    label="Fecha y hora de fin *"
                    labelAbove
                    placeholder=""
                    additionalClasses="bg-surface-base"
                    errMsg={errors.ends_at?.message}
                  />

                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-3">
                    <Input
                      {...register('place')}
                      id="place"
                      label="Lugar *"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.place?.message}
                    />
                    <Input
                      {...register('address')}
                      id="address"
                      label="Dirección del evento *"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.address?.message}
                    />
                    <Input
                      {...register('phone')}
                      id="phone"
                      label="Teléfono de contacto *"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.phone?.message}
                    />
                    <Input
                      {...register('min_age')}
                      id="min_age"
                      type="number"
                      label="Edad mínima *"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.min_age?.message}
                    />
                  </div>
                  <div className="flex flex-col gap-3 w-full">
                    {userRole === 'ROOT' && (
                      <div>
                        <div>
                          <Select
                            id='category'
                            options={
                              categories?.data
                                ?.filter((c) => !eventCategories?.data?.some((ec) => ec.id === c.id) && !category.some((ec) => ec.id === c.id))
                                ?.map((c) => ({
                                  label: c.name || '',
                                  value: c.id || '',
                                })) || []
                            }
                            label="Categoría"
                            placeholder="Selecciona la categoría"
                            onChange={(v) => setCategory([...category, categories?.data?.find((c) => c.id === v.target.value) as CategoryDTO])}
                          />
                        </div>
                        {category && category.length > 0 && (
                          <div className='flex flex-row gap-2 flex-wrap mt-2'>
                            {category.map((e) => (
                              <div
                                key={e.id}
                                className="p-1 bg-surface-secondary rounded-md border border-border-primary w-fit flex items-center gap-2"
                                onClick={() => setCategory(category.filter((se) => se.id !== e.id))}>
                                {e.name}
                                <IoClose className="h-4 w-4" />
                              </div>))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className='flex flex-col gap-1 w-full lg:max-w-xl'>
                      <div className="font-medium">
                        Imagen banner o portada
                      </div>
                      <p className="text-sm text-text-tertiary">Medidas recomendadas: 1920x700</p>
                      <PhotoUpload
                        id="banner_image"
                        className="w-full max-w-3xl"
                        imageClassName="aspect-[1920/700]"
                        onFileUploaded={(url) => {
                          setValue('banner_image', url);
                          toastSuccess('Imagen subida con éxito');
                        }}
                        defaultPreview={event?.banner_image || 'https://storage.googleapis.com/avantti-images/eventimg_59d38c70-fcb5-4ae1-9c57-4f8733345d78_avantti-default-eventpng'}
                      />
                      <ErrorMessage message={errors.banner_image?.message} />
                    </div>

                  </div>
                </div>
                {userRole === 'ROOT' && (
                  <>
                    <FormControlTextArea
                      {...register('maps_link')}
                      id="maps_link"
                      label="Enlace de Google Maps"
                      labelAbove
                      placeholder=""
                      errMsg={errors.maps_link?.message}
                    />
                    <Input
                      {...register('meta_pixel_id')}
                      id="meta_pixel_id"
                      label="Meta Pixel ID"
                      labelAbove
                      placeholder="Ej: 123456789012345"
                      errMsg={errors.meta_pixel_id?.message}
                    />
                  </>
                )}

              </div >

              <Accordion title="Ajustes avanzados" padding="p-2 lg:p-6" titleClassName="text-2xl font-medium">
                <div>
                  <div className="flex flex-col gap-2 mb-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col gap-2 mb-2">
                        <div className="text-xl font-medium">
                          Identificación de entradas
                        </div>
                        <div>
                          Los eventos configurados bajo esta condición requieren la
                          identificación de entradas al momento que el usuario asiste
                        </div>
                        <div className="flex flex-col gap-2">
                          <Switch
                            label="Obligatorio"
                            labelClassName="font-medium"
                            checked={watch('require_id_tickets')}
                            onChange={(e) => setValue('require_id_tickets', e.target.checked)}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 mb-2">
                        <div className="font-medium   text-xl">
                          Permisos de vendedores
                        </div>
                        <div>
                          Habilita o deshabilita las ventas a través de vendedores para este evento
                        </div>
                        <div className="flex flex-col gap-2">
                          <Switch
                            label="Habilitar ventas por vendedores"
                            labelClassName="font-medium"
                            checked={watch('seller_sales_enabled')}
                            onChange={(e) => setValue('seller_sales_enabled', e.target.checked)}
                          />
                          <Switch
                            label="Permitir que vendedores descarguen entradas"
                            labelClassName="font-medium"
                            checked={watch('seller_can_download_tickets')}
                            onChange={(e) => setValue('seller_can_download_tickets', e.target.checked)}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 mb-2">
                        <div className=" text-xl font-medium">Configuración del evento</div>
                        <div>
                          Configuración avanzada general del evento
                        </div>
                        <div className="flex flex-col gap-2">
                          <Input
                            {...register('business_name')}
                            id="business_name"
                            label="Nombre de la productora *"
                            labelAbove
                            placeholder=""
                            additionalClasses="bg-surface-base"
                            errMsg={errors.business_name?.message}
                          />
                          <p className="text-sm text-text-tertiary">Ingresá el nombre de la productora que se mostrará en Avantti solo para este evento</p>
                          {userRole === 'ROOT' && (
                            <>
                              <Switch
                                label="Aprobado"
                                labelClassName="font-medium"
                                checked={watch('is_approved')}
                                disabled={!business?.is_reliable && userRole !== 'ROOT'}
                                onChange={(e) => setValue('is_approved', e.target.checked)}
                              />
                              <Switch
                                label="Evento destacado"
                                labelClassName="font-medium"
                                checked={watch('is_important')}
                                onChange={(e) => setValue('is_important', e.target.checked)}
                              />
                              <Switch
                                label="Entradas numeradas"
                                labelClassName="font-medium"
                                checked={watch('has_numerated_seats')}
                                onChange={(e) => setValue('has_numerated_seats', e.target.checked)}
                              />
                              <Switch
                                label="Entradas con descuentos por audiencia"
                                labelClassName="font-medium"
                                checked={watch('has_audience_discounts')}
                                onChange={(e) => setValue('has_audience_discounts', e.target.checked)}
                              />
                              <Switch
                                label="Entradas con precio abierto"
                                labelClassName="font-medium"
                                checked={watch('has_open_price')}
                                onChange={(e) => setValue('has_open_price', e.target.checked)}
                              />
                              <Switch
                                label="Tiene flujo simplificado de venta"
                                labelClassName="font-medium"
                                checked={watch('has_simplified_selling')}
                                onChange={(e) => setValue('has_simplified_selling', e.target.checked)}
                              />
                              <Switch
                                label="Tiene flujo de compra rápido"
                                labelClassName="font-medium"
                                checked={watch('superfast_checkout')}
                                onChange={(e) => setValue('superfast_checkout', e.target.checked)}
                              />
                              <Switch
                                label="Tiene funciones"
                                labelClassName="font-medium"
                                checked={watch('has_showtimes')}
                                onChange={(e) => setValue('has_showtimes', e.target.checked)}
                              />
                              <Switch
                                label="Notificar compras"
                                labelClassName="font-medium"
                                checked={watch('notify_purchases')}
                                onChange={(e) => setValue('notify_purchases', e.target.checked)}
                              />
                              <Switch
                                label="Tiene compradores promotores"
                                labelClassName="font-medium"
                                checked={watch('has_order_referrals') === 'enabled' || watch('has_order_referrals') === 'enabled_unaccepted'}
                                onChange={(e) => setValue('has_order_referrals', e.target.checked ? 'enabled_unaccepted' : 'disabled')}
                              />
                              <Switch
                                label="Mostrar ranking de vendedores"
                                labelClassName="font-medium"
                                checked={watch('show_sellers_ranking')}
                                onChange={(e) => setValue('show_sellers_ranking', e.target.checked)}
                              />
                              <Switch
                                label="Ordenes por WhatsApp"
                                labelClassName="font-medium"
                                checked={watch('order_phone_number')}
                                onChange={(e) => setValue('order_phone_number', e.target.checked)}
                              />
                              <Switch
                                label="Tiene listas"
                                labelClassName="font-medium"
                                checked={watch('has_lists')}
                                onChange={(e) => setValue('has_lists', e.target.checked)}
                              />
                              {business?.has_subscription && !create && (
                                <Switch
                                  label="Es una suscripción"
                                  labelClassName="font-medium"
                                  checked={watch('has_subscription')}
                                  onChange={(e) => setValue('has_subscription', e.target.checked)}
                                />
                              )}
                              {watch('has_subscription') && (
                                <div>
                                  <div className='max-w-md'>
                                    <Select
                                      id='related_events'
                                      options={options}
                                      label="Eventos relacionados"
                                      placeholder="Selecciona los eventos relacionados"
                                      onChange={(e) => setSubscribedEvents([...subscribedEvents, e.target.value])}
                                    />
                                  </div>
                                  {(subscribedEvents || (subscribedData?.length || 0) > 0) && (
                                    <div className='flex flex-row gap-2 flex-wrap mt-2'>
                                      {subscribedEvents.map((e) => (
                                        <div
                                          key={e}
                                          className="p-1 bg-surface-secondary rounded-md border border-border-primary w-fit flex items-center gap-2"
                                          onClick={() => setSubscribedEvents(subscribedEvents.filter((se) => se !== e))}>
                                          {business?.events?.find((s) => s.id === e)?.name}
                                          <IoClose className="h-4 w-4" />
                                        </div>))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {(userRole === 'ADMIN' || userRole === 'ROOT') && (
                            <>
                              <Switch
                                label="Coproductor puede acceder a liquidaciones"
                                labelClassName="font-medium"
                                checked={watch('coproducer_balance')}
                                onChange={(e) => setValue('coproducer_balance', e.target.checked)}
                              />
                              <Switch
                                label="Coproductor puede acceder a reportes"
                                labelClassName="font-medium"
                                checked={watch('coproducer_reports')}
                                onChange={(e) => setValue('coproducer_reports', e.target.checked)}
                              />
                            </>
                          )}
                        </div>
                      </div>
                      {userRole === 'ROOT' && (
                        <>

                          <div className="flex flex-col gap-2 mb-2">
                            <div className="font-medium   text-xl">
                              Recolección de datos
                            </div>
                            <div>
                              Esta opción habilita sumar una encuesta que completará el
                              usuario en el flujo de compra
                            </div>
                            <Switch
                              label="Habilitado QR de públicas"
                              labelClassName="font-medium"
                              checked={watch('has_seller_qr')}
                              onChange={(e) => setValue('has_seller_qr', e.target.checked)}
                            />
                            <Switch
                              label="Tiene encuesta"
                              labelClassName="font-medium"
                              checked={watch('has_polls')}
                              onChange={(e) => setValue('has_polls', e.target.checked)}
                            />
                          </div>
                        </>
                      )}
                      <div className="flex flex-col gap-2 mb-2">
                        <div className="font-medium   text-xl">
                          Configuración de escáner
                        </div>
                        <div>
                          Configura el comportamiento del escáner de entradas
                        </div>
                        <Switch
                          label="Auto aceptar QRs al escanear"
                          labelClassName="font-medium"
                          checked={watch('has_scanner_auto_accept')}
                          onChange={(e) => setValue('has_scanner_auto_accept', e.target.checked)
                          }
                        />
                        <Switch
                          label="QR único por compra"
                          labelClassName="font-medium"
                          checked={watch('has_order_qrs')}
                          onChange={(e) => setValue('has_order_qrs', e.target.checked)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Accordion>
            </>
          )
        }
        {
          tab === 'payments' && (
            <div className="glass-surface flex flex-col gap-3">
              <div className="flex flex-col gap-2 mb-2">
                <div className="font-medium text-xl">
                  Métodos de pago
                </div>
                <div>
                  Configurá las condiciones de pago bajo las que se venderán las
                  entradas de tu evento
                </div>
                <div className="flex flex-col gap-2">
                  <Switch
                    label="Recibe dinero de transferencias directas al productor"
                    labelClassName="font-medium"
                    checked={watch('receives_wire_transfers')}
                    onChange={(e) => setValue('receives_wire_transfers', e.target.checked)
                    }
                  />
                  <Switch
                    label="Acepta pagos de Mercado Pago"
                    labelClassName="font-medium"
                    checked={watch('allow_mercadopago_payments')}
                    onChange={(e) => setValue('allow_mercadopago_payments', e.target.checked)
                    }
                  />
                  <Switch
                    label="Acepta pagos de Payway"
                    labelClassName="font-medium"
                    checked={watch('allow_payway_payments')}
                    onChange={(e) => setValue('allow_payway_payments', e.target.checked)
                    }
                  />
                  <Switch
                    label="Acepta pagos de Uala"
                    labelClassName="font-medium"
                    checked={watch('allow_uala_payments')}
                    onChange={(e) => setValue('allow_uala_payments', e.target.checked)
                    }
                  />

                  <Switch
                    label="Acepta pagos de Nave"
                    labelClassName="font-medium"
                    checked={watch('allow_nave_payments')}
                    onChange={(e) => setValue('allow_nave_payments', e.target.checked)
                    }
                  />
                  <Switch
                    label="Acepta pagos por Transferencia Bancaria (Zilo)"
                    labelClassName="font-medium"
                    checked={watch('allow_zilo_payments')}
                    onChange={(e) => setValue('allow_zilo_payments', e.target.checked)}
                  />

                  <Switch
                    label="Acepta pagos con transferencias"
                    labelClassName="font-medium"
                    checked={watch('allow_manual_payments')}
                    onChange={(e) => setValue('allow_manual_payments', e.target.checked)
                    }
                  />
                  <Switch
                    label="Entradas incluyen service charge"
                    labelClassName="font-medium"
                    checked={watch('tickets_includes_service_charge')}
                    onChange={(e) => setValue('tickets_includes_service_charge', e.target.checked)}
                  />
                  <Switch
                    label="Acepta ventas presenciales"
                    labelClassName="font-medium"
                    checked={watch('allow_in_person_sales')}
                    onChange={(e) => setValue('allow_in_person_sales', e.target.checked)}
                  />
                  <Switch
                    label="Productor absorbe service charge"
                    labelClassName="font-medium"
                    checked={producerAbsorbsServiceCharge || ticketsIncludesServiceCharge}
                    onChange={(e) => setValue('producer_absorbs_service_charge', e.target.checked)}
                  />
                  {producerAbsorbsServiceCharge && (
                    <div className="w-full md:w-1/2">
                      <Input
                        {...register('absorbed_service_charge_rate')}
                        id="absorbed_service_charge_rate"
                        type="number"
                        step="0.0001"
                        label="% de service charge absorbido"
                        labelAbove
                        placeholder=""
                        additionalClasses="bg-surface-base"
                        errMsg={errors.absorbed_service_charge_rate?.message}
                        disabled={ticketsIncludesServiceCharge}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 mb-2">
                <div className="font-medium   text-xl">
                  Service charge
                </div>
                <div className="">
                  Configurá los porcentajes de costos del servicio que se suman al
                  monto por entrada
                </div>
                <div className="flex flex-row w-full gap-4">
                  <div className="w-1/2">
                    <Input
                      {...register('online_service_charge')}
                      id="online_service_charge"
                      type="number"
                      step="0.0001"
                      label="Service charge online"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.online_service_charge?.message}
                    />
                  </div>
                  <div className="w-1/2">
                    <Input
                      {...register('online_comission_rate')}
                      id="online_comission_rate"
                      type="number"
                      step="0.0001"
                      label="Comisión online"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.online_comission_rate?.message}
                    />
                  </div>
                  <div className="w-1/2">
                    <Input
                      {...register('manual_service_charge')}
                      id="manual_service_charge"
                      type="number"
                      step="0.0001"
                      label="Service charge manual"
                      labelAbove
                      placeholder=""
                      additionalClasses="bg-surface-base"
                      errMsg={errors.manual_service_charge?.message}
                    />
                  </div>
                </div>
              </div>
            </div>
          )
        }
        {
          !isOnBoarding && <div className="flex flex-row gap-4 justify-between mt-4">
            <Button
              type="button"
              variant="secondary"
              className="w-1/2"
              onClick={() => navigate('..')}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="w-1/2"
              loading={resCreateEvent.isLoading || resUpdateEvent.isLoading}
            >
              {create ? 'Crear' : 'Guardar'}
            </Button>
          </div>
        }
        {
          isOnBoarding && <div className="flex flex-row gap-4 justify-between mt-4">
            <Button
              type="button"
              variant="secondary"
              className="w-1/2"
              onClick={() => navigate(`/admin/${businessId}/events`)}
            >
              Omitir
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="w-1/2"
              loading={resCreateEvent.isLoading || resUpdateEvent.isLoading}
            >
              Crear evento
            </Button>
          </div>
        }
      </form >
      <CustomModal isOpen={isOnBoardingModalOpen} onRequestClose={() => { }} hideCancelButton>
        <div className='flex flex-col gap-2 max-w-[358px]'>
          <h1>¡Evento creado exitosamente!</h1>
          <p>¡Continuemos ahora creando una entrada para tu evento! En un simple paso podrás dar de alta el primer tipo de entrada de evento.</p>
          <p>Actualmente tu negocio no tiene entradas asociadas</p>
        </div>
        <div className="flex flex-row gap-4 justify-between mt-2">
          <Button
            type="button"
            variant="secondary"
            className="w-1/2"
            onClick={() => { navigate(`/admin/${businessId}/events/${createdEvent?.id}`); }}
          >
            Omitir
          </Button>
          <Button
            variant="primary"
            className="w-1/2"
            onClick={() => navigate(`/admin/onboarding/${businessId}/${createdEvent?.id}/create-ticket`)}
          >
            Crear entrada
          </Button>
        </div>
      </CustomModal>
    </div >
  );
};

export default EventEditor;
